import { useEffect, useState, useCallback } from "react";
import api, { errMsg, fmtDate, fmtQty, downloadReport } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, FileText, FileSpreadsheet } from "lucide-react";

const EMPTY = {
  equipment_id: "", failure_code: "", cause_code: "", event_date: "",
  description: "", downtime_hours: 0, wo_number: "",
  paro_start: "", paro_end: "", reason: "", affected_system: "",
  failure_report: "", activities_performed: "", notification_number: "",
  hourmeter: 0, shift: "", technicians_count: 1, hh_labor_hours: 0, supervisor: "",
};

const REASONS = ["correctivo","preventivo","predictivo","mala_operacion","otro"];
const REASON_LABEL = {correctivo:"Correctivo",preventivo:"Preventivo",predictivo:"Predictivo",mala_operacion:"Mala operación",otro:"Otro"};
const AFFECTED_SYSTEMS_FL = ["electrical","engine","transmission","hydraulic","pneumatic","brakes","structural","cooling","fuel","steering","cabin","other"];
const SYS_LABEL_FL = {electrical:"Eléctrico",engine:"Motor",transmission:"Transmisión",hydraulic:"Hidráulico",pneumatic:"Neumático",brakes:"Frenos",structural:"Estructural",cooling:"Refrigeración",fuel:"Combustible",steering:"Dirección",cabin:"Cabina",other:"Otro"};
const SHIFTS_FL = ["morning","afternoon","night"];
const SHIFT_LABEL_FL = {morning:"Matutino",afternoon:"Vespertino",night:"Nocturno"};

export default function MntFailureLog() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [causes, setCauses] = useState([]);
  const [eid, setEid] = useState("");
  const [df, setDf] = useState("");
  const [dt, setDt] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const editable = can("admin", "maintenance", "warehouse");

  const load = useCallback(() => {
    const params = {};
    if (eid) params.equipment_id = eid;
    if (df) params.date_from = df;
    if (dt) params.date_to = dt;
    api.get("/mnt/failure-log", { params }).then((r) => setRows(r.data));
  }, [eid, df, dt]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/equipment", { params: { active: true } }).then((r) => setEquipment(r.data));
    api.get("/mnt/failure-codes", { params: { category: "symptom" } }).then((r) => setSymptoms(r.data));
    api.get("/mnt/failure-codes", { params: { category: "cause" } }).then((r) => setCauses(r.data));
  }, []);

  const save = async () => {
    try {
      await api.post("/mnt/failure-log", { ...form, downtime_hours: parseFloat(form.downtime_hours) || 0 });
      toast.success(t("common.saved"));
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar registro?")) return;
    try { await api.delete(`/mnt/failure-log/${id}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const exportFile = async (fmt) => {
    try {
      const parts = [];
      if (eid) parts.push(`equipment_id=${eid}`);
      if (df) parts.push(`date_from=${df}`);
      if (dt) parts.push(`date_to=${dt}`);
      parts.push(`format=${fmt}`);
      await downloadReport(`/reports/mnt/failure-log?${parts.join("&")}`, `bitacora_fallas.${fmt === "xlsx" ? "xlsx" : fmt}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="mnt-failure-log-page">
      <PageHeader title={t("mnt.fl.title")} subtitle={t("mnt.fl.subtitle")}>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <Label className="text-[9px] font-bold uppercase tracking-widest">{t("mnt.equipment")}</Label>
            <Select value={eid || "all"} onValueChange={(v) => setEid(v === "all" ? "" : v)}>
              <SelectTrigger className="rounded-none h-8 text-xs w-56" data-testid="fl-eq-filter"><SelectValue placeholder={t("mnt.selectEquipment")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">— Todos —</SelectItem>
                {equipment.map((e) => <SelectItem key={e.id} value={e.id}>{e.code} · {e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[9px] font-bold uppercase tracking-widest">{t("mnt.h.dateFrom")}</Label>
            <Input type="date" value={df} onChange={(e) => setDf(e.target.value)} className="rounded-none h-8 text-xs w-36" data-testid="fl-date-from" />
          </div>
          <div>
            <Label className="text-[9px] font-bold uppercase tracking-widest">{t("mnt.h.dateTo")}</Label>
            <Input type="date" value={dt} onChange={(e) => setDt(e.target.value)} className="rounded-none h-8 text-xs w-36" data-testid="fl-date-to" />
          </div>
          <div className="flex border">
            <button onClick={() => exportFile("csv")} className="border-r px-2 h-8 text-[10px] font-bold hover:bg-muted flex items-center gap-1" data-testid="fl-export-csv"><FileText size={12} /> CSV</button>
            <button onClick={() => exportFile("xlsx")} className="px-2 h-8 text-[10px] font-bold hover:bg-muted flex items-center gap-1" data-testid="fl-export-xlsx"><FileSpreadsheet size={12} /> XLSX</button>
          </div>
          {editable && (
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(EMPTY); }}>
              <DialogTrigger asChild>
                <Button size="sm" className="rounded-none text-xs font-bold" data-testid="fl-new-button">
                  <Plus size={13} className="mr-1" /> {t("mnt.fl.new")}
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="uppercase font-black">{t("mnt.fl.new")}</DialogTitle>
                  <DialogDescription className="text-xs">{t("mnt.fl.subtitle")}</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="col-span-2 sm:col-span-3 space-y-1">
                    <Label className="text-[10px] font-bold uppercase">Equipo (N° Económico)</Label>
                    <Select value={form.equipment_id || "none"} onValueChange={(v) => setForm({ ...form, equipment_id: v === "none" ? "" : v })}>
                      <SelectTrigger className="rounded-none h-8 text-xs" data-testid="fl-form-equipment">
                        <SelectValue placeholder={t("mnt.selectEquipment")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {equipment.map((e) => <SelectItem key={e.id} value={e.id}>{e.code} · {e.name}{e.model ? ` · ${e.model}` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase">Hora de paro</Label>
                    <Input type="datetime-local" value={form.paro_start} onChange={(e) => setForm({ ...form, paro_start: e.target.value })}
                      className="rounded-none h-8 text-xs" data-testid="fl-form-paro-start" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase">Hora disponible</Label>
                    <Input type="datetime-local" value={form.paro_end} onChange={(e) => setForm({ ...form, paro_end: e.target.value })}
                      className="rounded-none h-8 text-xs" data-testid="fl-form-paro-end" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase">Duración (auto)</Label>
                    <Input readOnly value={(() => {
                      if (!form.paro_start || !form.paro_end) return "—";
                      const d = (new Date(form.paro_end) - new Date(form.paro_start)) / 3600000;
                      return isFinite(d) && d > 0 ? `${d.toFixed(2)} h` : "—";
                    })()}
                    className="rounded-none h-8 text-xs bg-muted tabular-nums font-bold" data-testid="fl-form-duration" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase">Razón de paro</Label>
                    <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                      className="w-full border h-8 text-xs bg-background" data-testid="fl-form-reason">
                      <option value="">—</option>
                      {REASONS.map(r => <option key={r} value={r}>{REASON_LABEL[r]}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase">Sistema afectado</Label>
                    <select value={form.affected_system} onChange={(e) => setForm({ ...form, affected_system: e.target.value })}
                      className="w-full border h-8 text-xs bg-background" data-testid="fl-form-system">
                      <option value="">—</option>
                      {AFFECTED_SYSTEMS_FL.map(s => <option key={s} value={s}>{SYS_LABEL_FL[s]}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase">Turno</Label>
                    <select value={form.shift} onChange={(e) => setForm({ ...form, shift: e.target.value })}
                      className="w-full border h-8 text-xs bg-background" data-testid="fl-form-shift">
                      <option value="">—</option>
                      {SHIFTS_FL.map(s => <option key={s} value={s}>{SHIFT_LABEL_FL[s]}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase">Horómetro</Label>
                    <Input type="number" step="0.1" value={form.hourmeter} onChange={(e) => setForm({ ...form, hourmeter: e.target.value })}
                      className="rounded-none h-8 text-xs" data-testid="fl-form-hourmeter" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase">Cant. técnicos</Label>
                    <Input type="number" min="1" value={form.technicians_count} onChange={(e) => setForm({ ...form, technicians_count: e.target.value })}
                      className="rounded-none h-8 text-xs" data-testid="fl-form-tech-count" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase">H.H (auto o manual)</Label>
                    <Input type="number" step="0.1" value={form.hh_labor_hours} onChange={(e) => setForm({ ...form, hh_labor_hours: e.target.value })}
                      className="rounded-none h-8 text-xs" placeholder="Se calcula auto" data-testid="fl-form-hh" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase">Supervisor</Label>
                    <Input value={form.supervisor} onChange={(e) => setForm({ ...form, supervisor: e.target.value })}
                      className="rounded-none h-8 text-xs" data-testid="fl-form-supervisor" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase">Código de falla</Label>
                    <Select value={form.failure_code || "none"} onValueChange={(v) => setForm({ ...form, failure_code: v === "none" ? "" : v })}>
                      <SelectTrigger className="rounded-none h-8 text-xs" data-testid="fl-form-code"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {symptoms.map((s) => <SelectItem key={s.id} value={s.code}>{s.code} · {s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase">Causa</Label>
                    <Select value={form.cause_code || "none"} onValueChange={(v) => setForm({ ...form, cause_code: v === "none" ? "" : v })}>
                      <SelectTrigger className="rounded-none h-8 text-xs" data-testid="fl-form-cause"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {causes.map((c) => <SelectItem key={c.id} value={c.code}>{c.code} · {c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase">Aviso N°</Label>
                    <Input value={form.notification_number} onChange={(e) => setForm({ ...form, notification_number: e.target.value })}
                      className="rounded-none h-8 text-xs" placeholder="AV-XXXXX" data-testid="fl-form-notif" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase">OT relacionada</Label>
                    <Input value={form.wo_number} onChange={(e) => setForm({ ...form, wo_number: e.target.value })}
                      className="rounded-none h-8 text-xs" placeholder="OT-XXXXX" data-testid="fl-form-wo" />
                  </div>
                  <div className="col-span-2 sm:col-span-3 space-y-1">
                    <Label className="text-[10px] font-bold uppercase">Reporte de falla</Label>
                    <Textarea rows={2} value={form.failure_report} onChange={(e) => setForm({ ...form, failure_report: e.target.value })}
                      className="rounded-none text-xs" data-testid="fl-form-report" />
                  </div>
                  <div className="col-span-2 sm:col-span-3 space-y-1">
                    <Label className="text-[10px] font-bold uppercase">Actividades realizadas</Label>
                    <Textarea rows={2} value={form.activities_performed} onChange={(e) => setForm({ ...form, activities_performed: e.target.value })}
                      className="rounded-none text-xs" data-testid="fl-form-activities" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={save} disabled={!form.equipment_id}
                    className="rounded-none font-bold text-xs" data-testid="fl-save-button">{t("common.save")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </PageHeader>

      {rows.length === 0 ? <Empty text={t("common.noData")} /> : (
        <div className="tbl-wrap" data-testid="fl-table">
          <table className="tbl">
            <thead><tr>
              <th>Fecha</th><th>N° Eco</th><th>Equipo</th>
              <th>Razón</th><th>Sistema</th><th>Duración</th>
              <th>H.H</th><th>Turno</th><th>Supervisor</th>
              <th>OT</th><th>Aviso</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`fl-row-${r.id}`}>
                  <td className="text-[10px] tabular-nums">{fmtDate(r.event_date)}</td>
                  <td className="font-bold tabular-nums">{r.equipment_code}</td>
                  <td className="text-[11px]">{r.equipment_name}<span className="text-[10px] text-muted-foreground"> · {r.equipment_model || "—"}</span></td>
                  <td className="text-[10px] font-bold uppercase">{r.reason || "—"}</td>
                  <td className="text-[10px]">{r.affected_system || "—"}</td>
                  <td className="tabular-nums font-bold">{fmtQty(r.downtime_hours)} h</td>
                  <td className="tabular-nums">{fmtQty(r.hh_labor_hours || 0)} h</td>
                  <td className="text-[10px]">{r.shift || "—"}</td>
                  <td className="text-[11px]">{r.supervisor || "—"}</td>
                  <td className="text-[10px] font-bold">{r.wo_number || "—"}</td>
                  <td className="text-[10px]">{r.notification_number || "—"}</td>
                  <td>{editable && (
                    <button className="text-muted-foreground hover:text-destructive" onClick={() => del(r.id)} data-testid={`fl-del-${r.id}`}>
                      <Trash2 size={12} />
                    </button>
                  )}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
